import React, {ReactNode, useCallback, useContext, useEffect, useState} from "react";
import {parse, stringify} from "query-string";
import {useLocation, useNavigate} from "react-router";

export interface SubPagesState {
  selectedSubPage: string | undefined,
  selectSubPage: (subPage: string | undefined, replaceHistoryEntry?: boolean) => void
}

export const SubPagesContext = React.createContext<SubPagesState | undefined>(undefined);

export function useSubPages(): SubPagesState {
  const subPagesContext = useContext(SubPagesContext);
  if (!subPagesContext) {
    throw new Error('useSubPages must be used within the SubPagesContext.Provider');
  }
  return subPagesContext;
}

export function SubPagesProvider(props: {
  defaultSubPage?: string,
  children?: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  // The default is only applied on mount. Deriving it again on every render would reset the
  // selection whenever the default changes for unrelated reasons (e.g. a breakpoint switch on
  // resize), unmounting the sub page and discarding unsaved form input. See SSO-237.
  const [selectedSubPage, setSelectedSubPage] = useState<string | undefined>(props.defaultSubPage);
  const subPageKey = 'subPage';

  useEffect(() => {
    const parsed = parse(location.search);
    if (parsed[subPageKey]) {
      setSelectedSubPage(parsed[subPageKey] as string)
      // console.info("Got subPage from location: " + parsed[subPageKey]);
    }
  }, [location]);

  const selectSubPage = useCallback((subPage: string | undefined, replaceHistoryEntry?: boolean) => {
    // console.info("Selecting sub page " + subPage);
    setSelectedSubPage(subPage);
    const parsed = parse(location.search);
    parsed[subPageKey] = subPage || null;
    location.search = stringify(parsed, {skipNull: true});
    navigate(location, {replace: replaceHistoryEntry});
  }, [location, navigate]);

  return (
    <SubPagesContext.Provider value={{selectedSubPage: selectedSubPage, selectSubPage: selectSubPage} as SubPagesState}>
      {props.children}
    </SubPagesContext.Provider>
  )
}
